import React from 'react';
import { KeyboardKey } from './KeyboardKey';
import { HEBREW_ENGLISH_KEYBOARD } from '../constants/keyboard';
import { KeyboardDifficulty } from '../config';

interface KeyboardDisplayProps {
    activeKey: string | null;
    onKeyPress: (key: string) => void;
    currentWord?: string;
    keyboardDifficulty?: KeyboardDifficulty;
    currentLetterIndex?: number;
    onKeyRef?: (letter: string, element: HTMLButtonElement | null) => void;
    keysInAnimation?: Set<string>;
}

export const KeyboardDisplay: React.FC<KeyboardDisplayProps> = ({ activeKey, onKeyPress, currentWord = '', keyboardDifficulty = 'full', currentLetterIndex = 0, onKeyRef, keysInAnimation = new Set() }) => {
    // Only treat phones as mobile, not tablets
    const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 768;
    const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
    
    // Get all Hebrew letters from keyboard for random selection
    const getAllHebrewLetters = () => {
        const allLetters: string[] = [];
        HEBREW_ENGLISH_KEYBOARD.forEach(row => {
            row.forEach(key => {
                if (key.he !== ' ' && key.he !== 'SPACE') {
                    allLetters.push(key.he);
                }
            });
        });
        return allLetters;
    };

    const isKeyRelevant = (hebrewLetter: string) => {
        if (keyboardDifficulty === 'full') return true;
        if (hebrewLetter === ' ') return true; // Always show space bar
        
        // Get letters that still need to be typed (from current position onwards)
        const remainingWord = currentWord.slice(currentLetterIndex);
        const remainingLetters = new Set(remainingWord.split(''));
        
        if (keyboardDifficulty === 'adaptive') {
            return remainingLetters.has(hebrewLetter);
        }
        
        if (keyboardDifficulty === 'challenging') {
            // Include word letters + 3 random additional letters
            if (remainingLetters.has(hebrewLetter)) return true;
            
            // Add 3 random letters that are not in the word (same for entire word)
            const allLetters = getAllHebrewLetters();
            const wordLetters = new Set(currentWord.split(''));
            const availableLetters = allLetters.filter(letter => !wordLetters.has(letter));
            
            // Use the word itself as seed for consistent randomization per word
            const seed = currentWord.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
            const shuffledLetters = [...availableLetters];
            
            // Simple shuffle based on word seed only
            for (let i = shuffledLetters.length - 1; i > 0; i--) {
                const j = (seed + i) % (i + 1);
                [shuffledLetters[i], shuffledLetters[j]] = [shuffledLetters[j], shuffledLetters[i]];
            }
            
            const additionalLetters = shuffledLetters.slice(0, 3);
            return additionalLetters.includes(hebrewLetter);
        }
        
        return true;
    };
    
    return (
    <div style={{
        backgroundColor: '#111827',
        padding: isMobile ? '16px 8px' : (isTablet ? '24px 16px' : '32px'), // Medium padding for tablets
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
        border: '2px solid #374151',
        position: 'relative',
        maxWidth: '100%', // Prevent overflow
        overflow: 'visible' // Allow animations to show
    }}>
        {HEBREW_ENGLISH_KEYBOARD.map((row, rowIndex) => (
            <div key={rowIndex} style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '8px',
                marginLeft: '0px',
                gap: isTablet ? '12px' : (isMobile ? '4px' : '10px')
            }}>
                {row.map((keyData) => (
                    keyData.en === 'SPACE' ? (
                        <button
                            key={keyData.he}
                            onClick={() => onKeyPress(keyData.he)}
                            style={{
                                padding: isMobile ? '12px 40px' : (isTablet ? '16px 120px' : '16px 160px'), // Slightly smaller spacebar for tablets
                                margin: isMobile ? '2px' : '0px', // No margin - using CSS gap instead
                                borderRadius: '6px',
                                border: `2px solid ${activeKey === keyData.he ? '#fbbf24' : '#374151'}`,
                                backgroundColor: activeKey === keyData.he ? '#fbbf24' : '#1f2937',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                transform: activeKey === keyData.he ? 'scale(1.05)' : 'scale(1)',
                                boxShadow: activeKey === keyData.he 
                                    ? '0 0 20px rgba(251, 191, 36, 0.6), 0 10px 25px rgba(0,0,0,0.3)' 
                                    : '0 2px 4px rgba(0,0,0,0.3)',
                                fontSize: isMobile ? '14px' : '16px', // Same font size for tablets and desktop
                                fontWeight: 'bold',
                                color: activeKey === keyData.he ? '#1f2937' : '#ffffff',
                                opacity: isKeyRelevant(keyData.he) ? 1 : 0.2
                            }}
                        >
                            SPACE
                        </button>
                    ) : (
                        <KeyboardKey
                            key={keyData.he}
                            hebrewLetter={keyData.he}
                            englishLetter={keyData.en}
                            isActive={activeKey === keyData.he && !keysInAnimation.has(keyData.he)}
                            onClick={() => onKeyPress(keyData.he)}
                            isFaded={!isKeyRelevant(keyData.he)}
                            onKeyRef={(element) => onKeyRef?.(keyData.he, element)}
                        />
                    )
                ))}
            </div>
        ))}
        
        <div style={{
            position: 'absolute',
            top: isTablet ? '115px' : (isMobile ? '95px' : '118px'),
            left: '50%',
            transform: 'translateX(-50%)',
            width: isTablet ? '28px' : '24px',
            height: isTablet ? '28px' : '24px',
            backgroundColor: '#dc2626',
            borderRadius: '50%',
            border: '1px solid #991b1b',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            zIndex: 10
        }} />

    </div>
    );
};